export function parseDeviceUA(ua) {
  if (ua === undefined) {
    return {
      app: "Unknown device",
      version: "",
      platform: "",
      platformVersion: "",
      model: "",
      type: "unknown",
      isEmulator: false,
      raw: ua,
    };
  }

  if (!ua || typeof ua !== "string") {
    return {
      app: "Unknown",
      version: "",
      platform: "",
      platformVersion: "",
      model: "",
      type: "unknown",
      isEmulator: false,
      raw: ua,
    };
  }

  const isBrowser = /Mozilla\/5\.0/.test(ua);

  if (isBrowser) {
    const chrome = ua.match(/Chrome\/(\d+)/);
    const safari = ua.match(/Version\/(\d+).*Safari/);
    const firefox = ua.match(/Firefox\/(\d+)/);
    const browser = chrome
      ? `Chrome ${chrome[1]}`
      : firefox
        ? `Firefox ${firefox[1]}`
        : safari
          ? `Safari ${safari[1]}`
          : "Web";
    const platform = /Windows/.test(ua)
      ? "Windows"
      : /Macintosh|Mac OS X/.test(ua)
        ? "macOS"
        : /Linux/.test(ua)
          ? "Linux"
          : /iPad/.test(ua)
            ? "iPadOS"
            : /iPhone/.test(ua)
              ? "iOS"
              : /Android/.test(ua)
                ? "Android"
                : "Web";
    const type = /iPad/.test(ua)
      ? "tablet"
      : /iPhone|Android/.test(ua)
        ? "phone"
        : "desktop";

    return {
      app: "Web",
      version: browser,
      platform,
      platformVersion: "",
      model: "",
      type,
      isEmulator: false,
      raw: ua,
    };
  }

  const appMatch = ua.match(/^(\S+?)\/(\S+)\s*(?:\(([^)]*)\))?/);

  if (!appMatch) {
    return {
      app: "Unknown",
      version: "",
      platform: "",
      platformVersion: "",
      model: "",
      type: "unknown",
      isEmulator: false,
      raw: ua,
    };
  }

  const [, app, version, parenthetical = ""] = appMatch;
  const parts = parenthetical.split(";").map((s) => s.trim());
  const platformRaw = parts[0] || "";
  const rest = parts.slice(1).join(" ").trim();

  const platformMatch = platformRaw.match(
    /^(AndroidTV|Android|iOS|iPadOS|tvOS|macOS|Windows|Linux|FireTV)\s*(.+)?$/i
  );
  const platformKey = platformMatch?.[1] || platformRaw;
  const platform = /TV$/i.test(platformKey)
    ? platformKey.replace(/TV$/i, " TV")
    : platformKey;
  const platformVersion = platformMatch?.[2]?.trim() || "";

  const rawModel = rest.replace(/\s+/g, " ").trim();
  const archPattern = /(arm(64|v\d+|7)?$|x86(_64)?$|aarch\d+|amati)/i;
  const modelTokens = rawModel.split(" ").filter((t) => !archPattern.test(t));
  const model = modelTokens.slice(0, 2).join(" ");

  const type = /fire ?tv|android ?tv|tvos/i.test(platform)
    ? "tv"
    : /ipad/i.test(platform)
      ? "tablet"
      : /ios|iphone/i.test(platform)
        ? "phone"
        : /android/i.test(platform)
          ? "phone"
          : /mac|windows|linux/i.test(platform)
            ? "desktop"
            : "unknown";

  const isEmulator = /sdk_google|generic_x86|emulator|goldfish|ranchu/i.test(
    rawModel
  );

  return {
    app,
    version,
    platform,
    platformVersion,
    model,
    type,
    isEmulator,
    raw: ua,
  };
}

export function deviceIcon(type) {
  const map = {
    tv: "fa-tv",
    tablet: "fa-tablet-alt",
    phone: "fa-mobile-alt",
    desktop: "fa-desktop",
    unknown: "fa-question",
  };

  return map[type] || "fa-question";
}

// Amazon Fire device model identifiers → friendly labels.
// Source: developer.amazon.com/docs/device-specs/identify-fire-tv-devices.html
// Only adding codes that ship in real Lunova user-agent strings (sticks, cubes,
// boxes, OEM Fire TVs). Codes are lookup-by-uppercase via fireDeviceLabel().
const FIRE_DEVICE_MODELS = {
  // Fire TV Stick — HD line
  AFTB: "Fire TV (2014)",
  AFTM: "Fire TV Stick (2014)",
  AFTS: "Fire TV (2015)",
  AFTT: "Fire TV Stick (2016)",
  AFTN: "Fire TV (2017)",
  AFTSSS: "Fire TV Stick (2020)",
  AFTSS: "Fire TV Stick Lite (2020)",
  AFTBAMR311: "Fire TV Stick HD (2024)",
  AFTEAMR311: "Insignia HD Fire TV (2018-20)",
  AFTCL001: "Fire TV Stick HD (2026)",

  // Fire TV Stick — 4K line
  AFTMM: "Fire TV Stick 4K (2018)",
  AFTKA: "Fire TV Stick 4K Max (2021)",
  AFTKM: "Fire TV Stick 4K (2023)",
  AFTKRT: "Fire TV Stick 4K Max (2023)",
  AFTKMST12: "Toshiba 4K Fire TV (2018-19)",
  AFTKA002: "Fire TV 2-Series (2023)",
  AFTKAUK002: "Fire TV 2-Series UK (2023)",
  AFTCA002: "Fire TV Stick 4K Select (2025)",
  AFTMA08C15: "Fire TV Stick 4K Plus (2025)",

  // Fire TV Cube
  AFTA: "Fire TV Cube (2018)",
  AFTR: "Fire TV Cube (2019)",
  AFTGAZL: "Fire TV Cube (2022)",

  // Fire TV Edition — OEM smart TVs
  AFTRS: "Element 4K Fire TV (2017)",
  AFTJMST12: "Insignia 4K Fire TV (2018)",
  AFTEU011: "Grundig Vision 6 HD Fire TV (2019)",
  AFTEU014: "Grundig Vision 7 4K Fire TV (2019)",
  AFTEUFF014: "Grundig OLED 4K Fire TV (2019)",
  AFTSO001: "JVC 4K Fire TV (2019)",
  AFTLE: "Onida HD Fire TV (2019)",
  AFTBU001: "AmazonBasics HD/FHD Fire TV (2020)",
  AFTPR001: "AmazonBasics 4K Fire TV (2020)",
  AFTDCT31: "Toshiba/Insignia 4K Fire TV (2020)",
  AFTWI001: "ok 4K Fire TV (2020)",
  AFTWMST22: "JVC 2K Fire TV (2020)",
  AFTTIFF55: "Onida HD/FHD Fire TV (2020)",
  AFTTI43: "Fire TV 4-Series (2021)",
  AFTTIFF43: "Fire TV Omni Series (2021)",
  AFTHA001: "Hisense/Toshiba 4K UHD Fire TV (2021-22)",
  AFTHA002: "Toshiba V35 LED Fire TV (2021)",
  AFTHA003: "Toshiba 4K Far-field Fire TV (2021)",
  AFTHA004: "Hisense/Toshiba 4K UHD Fire TV (2022)",
  AFTANNA0: "Xiaomi F2 4K Fire TV (2022)",
  AFTJULI1: "JVC 4K Freeview Fire TV (2021)",
  AFTMON001: "Funai 4K Fire TV (2022)",
  AFTMON002: "Funai 4K Fire TV (2022)",
  AFTLBT962E2: "BMW Fire TV (2022)",
  AFTLFT962X3: "Stellantis Wagoneer Fire TV (2022)",
  AFTBOXE1: "Panasonic MX800 Fire TV (2023)",
  AFTBTX4: "Redmi 4K Fire TV (2023)",
  AFTSHN02: "TCL FHD Fire TV (2023)",
  AFTMD001: "TCL S4/Q6 4K Fire TV (2023)",
  AFTMD002: "TCL S3 1080p Fire TV (2023)",
  AFTWYM01: "Panasonic W-Series 4K Fire TV (2024-25)",
  AFTTOR001: "Panasonic Z-Series OLED Fire TV (2024-25)",
  AFTPOF46A5: "4K UHD Smart Fire TV (2024)",
  AFTSH3F7EF: "Regal 4K LED Fire TV (2024-25)",
  AFTMA475B1: "TCL 4K QLED Fire TV (2025)",
  AFTBR92D74: "Xiaomi 4K HDR Fire TV (2026)",
  AFTHA78451: "Hisense U7 Fire TV (2026)",
  AFTCHE92D8: "TCL QM64L QD-Mini LED Fire TV (2026)",
  AFTAR04B9A: "Onida 4K Smart Fire TV (2026)",
  AFTPOC9BD3: "Fire TV Omni QLED Series (2025)",
  AFTPO9D4D7: "Fire TV Omni QLED Series (2025)",
  AFTCU864A2: "Fire TV 2-Series (2025)",
  AFTCUD6595: "Fire TV 2-Series (2025)",
  AFTNA1322D: "Fire TV 4-Series (2025)",
  AFTNA67200: "Fire TV 4-Series (2025)",
  AFTBOE497A: "Amazon Ember Artline (2026)",
  AFTBOEF7DA: "Amazon Ember Artline (2026)",
};

// Non-Amazon Android TV / Google TV / streaming device model identifiers.
// Keyed by uppercase model token from parseDeviceUA. Manufacturer-only fallbacks
// live in DEVICE_MANUFACTURER_LABELS below.
const DEVICE_MODEL_LABELS = {
  // TCL Android TV / Google TV (no AFT code)
  "TCL SMART": "TCL Smart TV",
  "TCL TV": "TCL Smart TV",

  // Nvidia
  "SHIELD ANDROID TV": "NVIDIA Shield TV",
  SHIELD: "NVIDIA Shield TV",

  // Google / Chromecast
  CHROMECAST: "Chromecast w/ Google TV",
  SABRINA: "Chromecast w/ Google TV (4K)",
  KIRKWOOD: "Chromecast w/ Google TV (HD)",

  // Xiaomi / Mi Box
  MIBOX4: "Mi Box S",
  MIBOX3: "Mi Box (3rd Gen)",
  "MI TV STICK": "Mi TV Stick",

  // Walmart Onn streaming
  ONN_4K_STREAMING: "Onn 4K Streaming Box",
  "ONN STREAMING BOX": "Onn Streaming Box",

  // Roku
  ROKU: "Roku Device",

  // Apple TV
  "APPLE TV": "Apple TV",
  APPLETV: "Apple TV",
};

// Tailwind class strings for platform badges. Each entry pairs light and dark
// styles so badges stay legible in either mode. Keep in sync with the
// `parsed.platform` values produced by parseDeviceUA above.
const PLATFORM_BADGES = {
  Web: {
    label: "Web",
    classes:
      "bg-blue-100 text-blue-700 ring-blue-300 dark:bg-blue-500/10 dark:text-blue-300 dark:ring-blue-500/30",
  },
  macOS: {
    label: "macOS",
    classes:
      "bg-slate-100 text-slate-700 ring-slate-300 dark:bg-slate-500/15 dark:text-slate-200 dark:ring-slate-400/30",
  },
  Windows: {
    label: "Windows",
    classes:
      "bg-sky-100 text-sky-700 ring-sky-300 dark:bg-sky-500/10 dark:text-sky-300 dark:ring-sky-500/30",
  },
  Linux: {
    label: "Linux",
    classes:
      "bg-yellow-100 text-yellow-800 ring-yellow-300 dark:bg-yellow-500/10 dark:text-yellow-300 dark:ring-yellow-500/30",
  },
  iOS: {
    label: "iOS",
    classes:
      "bg-zinc-100 text-zinc-700 ring-zinc-300 dark:bg-zinc-500/15 dark:text-zinc-200 dark:ring-zinc-400/30",
  },
  iPadOS: {
    label: "iPadOS",
    classes:
      "bg-zinc-100 text-zinc-700 ring-zinc-300 dark:bg-zinc-500/15 dark:text-zinc-200 dark:ring-zinc-400/30",
  },
  tvOS: {
    label: "tvOS",
    classes:
      "bg-zinc-100 text-zinc-700 ring-zinc-300 dark:bg-zinc-500/15 dark:text-zinc-200 dark:ring-zinc-400/30",
  },
  Android: {
    label: "Android",
    classes:
      "bg-emerald-100 text-emerald-700 ring-emerald-300 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-500/30",
  },
  "Android TV": {
    label: "Android TV",
    classes:
      "bg-emerald-100 text-emerald-700 ring-emerald-300 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-500/30",
  },
  "Fire TV": {
    label: "Fire TV",
    classes:
      "bg-orange-100 text-orange-700 ring-orange-300 dark:bg-orange-500/10 dark:text-orange-300 dark:ring-orange-500/30",
  },
};

const UNKNOWN_PLATFORM_BADGE = {
  label: "Unknown",
  classes:
    "bg-gray-100 text-gray-700 ring-gray-300 dark:bg-gray-500/15 dark:text-gray-300 dark:ring-gray-400/30",
};

// Returns a { label, classes } pair describing the colored pill for a given
// parsed platform. Falls back to a neutral "Unknown" pill when the platform is
// missing, and to a neutral pill labelled with the raw platform string when the
// platform is recognized by parseDeviceUA but not in the badge map.
export function platformBadge(platform) {
  if (!platform) return UNKNOWN_PLATFORM_BADGE;

  return (
    PLATFORM_BADGES[platform] || {
      label: platform,
      classes: UNKNOWN_PLATFORM_BADGE.classes,
    }
  );
}

// Returns the friendly label for a known device model, or null otherwise.
// Checks Amazon Fire codes (AFT*) first, then non-Amazon Android TV / Google TV
// / streaming-device identifiers (TCL Smart, NVIDIA Shield, Chromecast, etc).
export function fireDeviceLabel(model) {
  if (!model) return null;

  // Strip leading "Amazon " if present, normalize whitespace + case
  const normalized = String(model)
    .replace(/^Amazon\s+/i, "")
    .replace(/\s+/g, " ")
    .trim()
    .toUpperCase();

  return (
    FIRE_DEVICE_MODELS[normalized] || DEVICE_MODEL_LABELS[normalized] || null
  );
}
